import { Image as ImagemIcone, Minus, Type } from 'lucide-react';

import { LayoutDocumentoResponseMarcaDaguaTipo as Tipo } from '@/api/generated/model';
import { CampoDeImagem } from '@/shared/components/CampoDeImagem';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Input } from '@/shared/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

export interface EstadoDaMarcaDagua {
  tipo: (typeof Tipo)[keyof typeof Tipo];
  texto: string;
  arquivoId: string | null;
  url: string | null;
  opacidade: number;
  inclinacao: number;
}

/**
 * Marca d'água: nenhuma, texto ou imagem.
 *
 * ⚠️ **A imagem sobe no instante em que é escolhida**, não no "Salvar" — o
 * mesmo contrato que `AssinaturaCard` já tem para a rubrica. É o que permite a
 * prévia em PDF de um layout ainda não salvo: quando o médico clica em "Prévia",
 * o `arquivoId` já existe.
 *
 * Opacidade e inclinação são `<input type="range">` NATIVO: não existe `slider`
 * em `shared/ui/` e `@radix-ui/react-slider` não está instalado. `accent-brand`
 * do Tailwind colore o range nativo.
 */
export function ControleDeMarcaDagua({
  valor,
  aoMudar,
  aoEnviarImagem,
  enviando,
}: {
  valor: EstadoDaMarcaDagua;
  aoMudar: (parcial: Partial<EstadoDaMarcaDagua>) => void;
  aoEnviarImagem: (arquivo: File) => void;
  enviando: boolean;
}) {
  const temMarca = valor.tipo !== Tipo.NENHUMA;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-brand">Marca d'água</p>

      <Tabs value={valor.tipo} onValueChange={(v) => aoMudar({ tipo: v as EstadoDaMarcaDagua['tipo'] })}>
        <TabsList className="grid grid-cols-3 h-auto">
          <TabsTrigger value={Tipo.NENHUMA} className="gap-2">
            <Minus className="w-4 h-4" />
            Nenhuma
          </TabsTrigger>
          <TabsTrigger value={Tipo.TEXTO} className="gap-2">
            <Type className="w-4 h-4" />
            Texto
          </TabsTrigger>
          <TabsTrigger value={Tipo.IMAGEM} className="gap-2">
            <ImagemIcone className="w-4 h-4" />
            Imagem
          </TabsTrigger>
        </TabsList>

        <TabsContent value={Tipo.NENHUMA} className="pt-3">
          <p className="text-sm text-muted-foreground">
            O documento sai sem nada atrás do texto.
          </p>
        </TabsContent>

        <TabsContent value={Tipo.TEXTO} className="pt-3">
          <Campo
            id="marca-dagua-texto"
            rotulo="Texto"
            // O limite não é estético — ver o DTO no backend.
            dica="Até 24 caracteres. Textos maiores quebram linha e deixam de parecer uma faixa."
          >
            <Input
              id="marca-dagua-texto"
              value={valor.texto}
              maxLength={24}
              placeholder="CÓPIA"
              onChange={(e) => aoMudar({ texto: e.target.value })}
              className={CLASSE_CONTROLE}
            />
          </Campo>
        </TabsContent>

        <TabsContent value={Tipo.IMAGEM} className="pt-3">
          <CampoDeImagem
            rotulo="Imagem da marca d'água"
            ajuda="PNG ou JPG (RGB), ao menos 400px de largura, até 1 MB. O @react-pdf não lê WebP — a imagem sumiria do PDF sem avisar."
            urlAtual={valor.url}
            proporcao="aspect-square"
            enviando={enviando}
            aoEnviar={aoEnviarImagem}
          />
        </TabsContent>
      </Tabs>

      {temMarca && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <Deslizante
            id="marca-dagua-opacidade"
            rotulo="Opacidade"
            valorVisivel={`${valor.opacidade}%`}
            min={3}
            max={25}
            passo={1}
            valor={valor.opacidade}
            aoMudar={(opacidade) => aoMudar({ opacidade })}
            // A faixa é a útil: abaixo de 3% some na impressão, acima de 25%
            // compete com o texto do documento.
            dica="3% a 25%"
          />
          <Deslizante
            id="marca-dagua-inclinacao"
            rotulo="Inclinação"
            valorVisivel={rotuloDaInclinacao(valor.inclinacao)}
            min={-90}
            max={90}
            passo={15}
            valor={valor.inclinacao}
            aoMudar={(inclinacao) => aoMudar({ inclinacao })}
            dica="0° é horizontal"
          />
        </div>
      )}
    </div>
  );
}

function rotuloDaInclinacao(graus: number): string {
  if (graus === 0) return 'Horizontal';
  if (graus === 90 || graus === -90) return 'Vertical';
  return `${graus}°`;
}

function Deslizante({
  id,
  rotulo,
  valorVisivel,
  min,
  max,
  passo,
  valor,
  aoMudar,
  dica,
}: {
  id: string;
  rotulo: string;
  valorVisivel: string;
  min: number;
  max: number;
  passo: number;
  valor: number;
  aoMudar: (valor: number) => void;
  dica: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="text-sm font-medium text-brand">
          {rotulo}
        </label>
        <span className="text-xs text-muted-foreground tabular-nums">{valorVisivel}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={passo}
        value={valor}
        onChange={(e) => aoMudar(Number(e.target.value))}
        className="w-full accent-brand cursor-pointer"
      />
      <p className="text-xs text-muted-foreground">{dica}</p>
    </div>
  );
}
